import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  answerQuestion,
  describeInstrument,
  getQuestion,
  listInstruments,
  readInstrument,
  recordEvent,
  startSession,
  submitAssessment,
  type Contact,
  type Instrument,
  type Question,
  type SubmitResponse,
} from "./api";
import { Gate } from "./components/Gate";
import { Intro } from "./components/Intro";
import { Picker } from "./components/Picker";
import { QuestionScreen } from "./components/QuestionScreen";
import { Results } from "./components/Results";
import { Verifying } from "./components/Verifying";
import { fetchChallenge, solveChallenge } from "./pow";
import { SHOW_ADMIN_LINK } from "./demoFlags";
import { Warn } from "./components/icons";

type Stage = "picker" | "intro" | "verifying" | "question" | "gate" | "results";

export function App() {
  const [stage, setStage] = useState<Stage>("intro");
  const [token, setToken] = useState("");
  const [total, setTotal] = useState(32);
  const [question, setQuestion] = useState<Question | null>(null);
  const [answered, setAnswered] = useState(0);
  const [result, setResult] = useState<SubmitResponse | null>(null);
  const [email, setEmail] = useState("");

  // Which assessment this run is. Null until described — the intro screen falls
  // back to the TRA's own shape, which is what it always showed.
  const [instrument, setInstrument] = useState<Instrument | null>(null);
  const [choices, setChoices] = useState<Instrument[]>([]);
  // True when the visitor picked rather than arriving on a ?test= link. Only
  // then does going "back" to the picker mean anything.
  const [cameFromPicker, setCameFromPicker] = useState(false);

  const [busy, setBusy] = useState(false);
  const [powProgress, setPowProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const startedAt = useRef(Date.now());

  /** Turn any thrown error into something a visitor can act on. */
  const handleError = useCallback((err: unknown) => {
    if (err instanceof ApiError) {
      setFieldErrors(err.fieldErrors);
      if (err.isSessionGone) {
        setError("Your session timed out. We'll need to start again — sorry about that.");
        setStage("intro");
        setToken("");
        setQuestion(null);
        setAnswered(0);
        return;
      }
      // Field-level problems are shown inline by the form, not as a banner.
      if (Object.keys(err.fieldErrors).length === 0) setError(err.message);
      return;
    }
    setError("Something went wrong. Please try again.");
  }, []);

  const begin = useCallback(async () => {
    setBusy(true);
    setError(null);
    setPowProgress(0);
    try {
      // The server decides whether work is required. difficulty 0 means it's
      // off — skip the screen rather than flash an empty progress moment.
      // See docs/BOT_DEFENCE.md for why it defaults to off.
      const challenge = await fetchChallenge();

      let solved = { ...challenge, solution: "" };
      if (challenge.difficulty > 0) {
        setStage("verifying");
        solved = await solveChallenge(challenge, setPowProgress);
        // Let the completed mark land before moving on; without this the
        // animation finishes and vanishes in the same frame.
        await new Promise((resolve) => setTimeout(resolve, 260));
      }

      const session = await startSession(solved, instrument?.slug);
      startedAt.current = Date.now();
      setToken(session.session_token);
      setTotal(session.total_questions);
      setQuestion(session.question);
      setAnswered(0);
      setStage("question");
    } catch (err) {
      setStage("intro");
      handleError(err);
    } finally {
      setBusy(false);
    }
  }, [handleError, instrument]);

  const answer = useCallback(
    async (optionId: string) => {
      if (!question) return;
      setBusy(true);
      setError(null);
      try {
        const res = await answerQuestion(token, question.id, optionId);
        setAnswered(res.answered_count);
        setTotal(res.total_questions);

        if (res.next_question) {
          setQuestion(res.next_question);
        } else {
          recordEvent(token, "gate_reached");
          setStage("gate");
        }
      } catch (err) {
        handleError(err);
      } finally {
        setBusy(false);
      }
    },
    [handleError, question, token],
  );

  /** Back re-fetches by position so a previously chosen answer is restored. */
  const goBack = useCallback(
    async (targetPosition: number) => {
      if (targetPosition < 1) return;
      setBusy(true);
      setError(null);
      try {
        setQuestion(await getQuestion(token, targetPosition));
        setStage("question");
      } catch (err) {
        handleError(err);
      } finally {
        setBusy(false);
      }
    },
    [handleError, token],
  );

  const submit = useCallback(
    async (contact: Contact) => {
      setBusy(true);
      setError(null);
      setFieldErrors({});
      try {
        const res = await submitAssessment(token, contact);
        setEmail(contact.email);
        setResult(res);
        setStage("results");
        recordEvent(token, "completed");
      } catch (err) {
        handleError(err);
      } finally {
        setBusy(false);
      }
    },
    [handleError, token],
  );

  const restart = useCallback(() => {
    setStage(cameFromPicker ? "picker" : "intro");
    setToken("");
    setQuestion(null);
    setResult(null);
    setAnswered(0);
    setError(null);
    setFieldErrors({});
  }, [cameFromPicker]);

  // Tell the parent Wix page how tall we are, so the iframe never gets its own
  // scrollbar. The snippet in wix/ listens for this.
  useEffect(() => {
    const post = () => {
      try {
        window.parent.postMessage(
          { type: "tra-resize", height: document.documentElement.scrollHeight },
          "*",
        );
      } catch {
        /* not embedded */
      }
    };
    post();
    const observer = new ResizeObserver(post);
    observer.observe(document.documentElement);
    return () => observer.disconnect();
  }, [stage, question, result, error]);

  // Scroll to the top on every screen change — in a tall iframe the visitor
  // would otherwise land mid-question.
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
  }, [stage, question?.id]);

  useEffect(() => {
    recordEvent("anonymous", "loaded");
  }, []);

  /**
   * Decide which assessment this is, once, on load.
   *
   * `?test=` on the embed URL wins — that is how Kim points a Wix page at a
   * particular assessment, and a visitor who followed that link has already
   * made the choice. With no parameter we ask what is on offer: more than one
   * means show the picker, exactly one means go straight into it.
   *
   * Every failure here falls through to the default assessment rather than
   * showing an error. A visitor who cannot be told which of four assessments
   * they are taking can still perfectly well take one.
   */
  useEffect(() => {
    let cancelled = false;
    const requested = readInstrument();

    if (requested) {
      describeInstrument(requested)
        .then((found) => !cancelled && setInstrument(found))
        .catch(() => {
          /* unknown slug: the server serves the default, and so do we */
        });
      return () => {
        cancelled = true;
      };
    }

    listInstruments()
      .then((available) => {
        if (cancelled) return;
        setChoices(available);
        if (available.length > 1) {
          setStage("picker");
        } else if (available.length === 1) {
          setInstrument(available[0] ?? null);
        }
      })
      .catch(() => {
        /* keep the intro screen; the default assessment still runs */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const choose = useCallback(
    (slug: string) => {
      setInstrument(choices.find((c) => c.slug === slug) ?? null);
      setCameFromPicker(true);
      setError(null);
      setStage("intro");
    },
    [choices],
  );

  const backToPicker = useCallback(() => {
    setInstrument(null);
    setCameFromPicker(false);
    setError(null);
    setStage("picker");
  }, []);

  const showProgress = stage === "question" || stage === "gate";
  const pct = stage === "gate" ? 100 : Math.round((answered / total) * 100);

  return (
    <div className="frame">
      {showProgress && (
        <div className="progress-wrap">
          <div className="progress-meta">
            <span>
              {stage === "gate"
                ? "Almost done"
                : `Question ${question?.position ?? 1} of ${total}`}
            </span>
            <span>{pct}%</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      <main className={`panel${stage === "question" ? " enter" : ""}`} tabIndex={-1}>
        {error && (
          <div className="hint" style={{ marginBottom: 18, marginTop: 0 }}>
            <Warn />
            <span>{error}</span>
          </div>
        )}

        {stage === "picker" && (
          <Picker instruments={choices} onChoose={choose} busy={busy} />
        )}

        {stage === "intro" && (
          <Intro
            onStart={begin}
            busy={busy}
            instrument={instrument}
            onBack={cameFromPicker ? backToPicker : undefined}
          />
        )}

        {stage === "verifying" && <Verifying progress={powProgress} />}

        {stage === "question" && question && (
          <QuestionScreen
            question={question}
            totalQuestions={total}
            busy={busy}
            onAnswer={answer}
            onBack={() => goBack((question.position ?? 1) - 1)}
            canGoBack={(question.position ?? 1) > 1}
            isLast={(question.position ?? 1) === total}
          />
        )}

        {stage === "gate" && (
          <Gate
            busy={busy}
            serverErrors={fieldErrors}
            onSubmit={submit}
            onBack={() => goBack(total)}
            startedAt={startedAt.current}
            total={total}
          />
        )}

        {stage === "results" && result && (
          <Results result={result} email={email} onRestart={restart} />
        )}
      </main>

      <p className="footnote">
        {stage === "question"
          ? `${answered} of ${total} answered`
          : `Mastery Partners · ${instrument?.label ?? "Transition Readiness Assessment"}`}
      </p>

      {/* Demo only — see src/demoFlags.ts. Hidden mid-assessment so it can
          never sit next to a question and pull someone out of their run. */}
      {SHOW_ADMIN_LINK && stage !== "question" && (
        <p className="admin-link">
          <a href="/admin.html" target="_blank" rel="noopener noreferrer">
            Admin dashboard
          </a>
        </p>
      )}
    </div>
  );
}
